import React, { Suspense, lazy } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "./contexts/AuthContext";
import PublicLayout from "./layouts/PublicLayout";
import AdminLayout from "./layouts/AdminLayout";
import AdminProtectedRoute from "./components/AdminProtectedRoute";
import AnalyticsTracker from "./components/AnalyticsTracker";

// Lazy loading komponen halaman untuk memangkas ukuran bundle awal
const Home = lazy(() => import("./pages/Home"));
const Products = lazy(() => import("./pages/Products"));
const ProductDetail = lazy(() => import("./pages/ProductDetail"));
const Gallery = lazy(() => import("./pages/Gallery"));
const Articles = lazy(() => import("./pages/Articles"));
const ArticleDetail = lazy(() => import("./pages/ArticleDetail"));
const Contact = lazy(() => import("./pages/Contact"));
const About = lazy(() => import("./pages/About"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const ProdukManagement = lazy(() => import("./pages/admin/ProdukManagement"));
const ProdukTambah = lazy(() => import("./pages/admin/ProdukTambah"));
const ProdukEdit = lazy(() => import("./pages/admin/ProdukEdit"));
const KategoriManagement = lazy(() => import("./pages/admin/KategoriManagement"));
const ManajemenHalaman = lazy(() => import("./pages/admin/ManajemenHalaman"));
const HalamanEdit = lazy(() => import("./pages/admin/HalamanEdit"));
const ManajemenUser = lazy(() => import("./pages/admin/ManajemenUser"));
const UserEdit = lazy(() => import("./pages/admin/UserEdit"));
const ArtikelManagement = lazy(() => import("./pages/admin/ArtikelManagement"));
const ArtikelEdit = lazy(() => import("./pages/admin/ArtikelEdit"));
const GaleriManagement = lazy(() => import("./pages/admin/GaleriManagement"));
const GaleriEdit = lazy(() => import("./pages/admin/GaleriEdit"));
const HalamanManagement = lazy(() => import("./pages/admin/HalamanManagement"));
const Settings = lazy(() => import("./pages/admin/Settings"));
const AdminLogin = lazy(() => import("./pages/admin/AdminLogin"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));

// Komponen loading sementara (fallback)
const PageLoader = () => (
  <div className="flex justify-center items-center h-screen bg-gray-50 dark:bg-[#171612]">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#d4af35]"></div>
  </div>
);

const App = () => {
  return (
    <AuthProvider>
      <AnalyticsTracker />
      <Suspense fallback={<PageLoader />}>
        <Routes>
          {/* Rute untuk halaman publik */}
          <Route path="/" element={<PublicLayout />}>
            <Route index element={<Home />} />
            <Route path="/products" element={<Products />} />
            <Route path="/product/:id" element={<ProductDetail />} />
            <Route path="/gallery" element={<Gallery />} />
            <Route path="/articles" element={<Articles />} />
            <Route path="/article/:id" element={<ArticleDetail />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/about" element={<About />} />
          </Route>

          {/* Rute untuk halaman admin login (tanpa layout) */}
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/admin/forgot-password" element={<ForgotPassword />} />

          {/* Rute untuk halaman admin dengan layout */}
          <Route path="/admin" element={<AdminLayout />}>
            <Route index element={<Navigate to="dashboard" replace />} />
            <Route
              path="dashboard"
              element={
                <AdminProtectedRoute>
                  <AdminDashboard />
                </AdminProtectedRoute>
              }
            />
            <Route
              path="products"
              element={
                <AdminProtectedRoute>
                  <ProdukManagement />
                </AdminProtectedRoute>
              }
            />
            <Route
              path="products/new"
              element={
                <AdminProtectedRoute>
                  <ProdukTambah />
                </AdminProtectedRoute>
              }
            />
            <Route
              path="products/:id/edit"
              element={
                <AdminProtectedRoute>
                  <ProdukEdit />
                </AdminProtectedRoute>
              }
            />
            <Route
              path="categories"
              element={
                <AdminProtectedRoute>
                  <KategoriManagement />
                </AdminProtectedRoute>
              }
            />
            <Route
              path="pages"
              element={
                <AdminProtectedRoute>
                  <ManajemenHalaman />
                </AdminProtectedRoute>
              }
            />
            <Route
              path="pages/:id/edit"
              element={
                <AdminProtectedRoute>
                  <HalamanEdit />
                </AdminProtectedRoute>
              }
            />
            <Route
              path="users"
              element={
                <AdminProtectedRoute>
                  <ManajemenUser />
                </AdminProtectedRoute>
              }
            />
            <Route
              path="users/:id/edit"
              element={
                <AdminProtectedRoute>
                  <UserEdit />
                </AdminProtectedRoute>
              }
            />
            <Route
              path="articles"
              element={
                <AdminProtectedRoute>
                  <ArtikelManagement />
                </AdminProtectedRoute>
              }
            />
            <Route
              path="articles/:id/edit"
              element={
                <AdminProtectedRoute>
                  <ArtikelEdit />
                </AdminProtectedRoute>
              }
            />
            <Route
              path="gallery"
              element={
                <AdminProtectedRoute>
                  <GaleriManagement />
                </AdminProtectedRoute>
              }
            />
            <Route
              path="gallery/:id/edit"
              element={
                <AdminProtectedRoute>
                  <GaleriEdit />
                </AdminProtectedRoute>
              }
            />
            <Route
              path="settings"
              element={
                <AdminProtectedRoute>
                  <Settings />
                </AdminProtectedRoute>
              }
            />
          </Route>

          {/* Redirect untuk rute yang tidak ditemukan */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </AuthProvider>
  );
};

export default App;
